import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { getValidToken } from "../../../utils/authUtils";
import { useReportFilter } from "../context/ReportFilterContext";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  formatCurrency,
  formatDate,
  formatVehicleNumber,
} from "../../../utils/utils";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import {
  PROJECT_TERMINOLOGY,
  ProjectTabEnum,
} from "../../../project/project_module/enums";
import { useState, useEffect } from "react";
import { Loading } from "../../../components/Loading";
import { OperationType } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import {
  CartesianGrid,
  Tooltip,
  XAxis,
  Legend,
  Bar,
  ResponsiveContainer,
  BarChart,
  YAxis,
  LabelList,
} from "recharts";

interface MaintenanceRecord {
  scope: "PROJECT" | "COMPANY";
  subScope: string;
  companyName: string;
  projectName: string;
  clientName: string;
  vehicleNumber: string;
  expenseDate: string;
  serviceType: string;
  paidTo: string;
  contactNumber: string;
  location: string;
  amount: string;
  superProjectName: string;
  superProjectId: string;
  projectId: string;
  expenseId: string;
}

interface SummaryItem {
  summaryType: "COMBINED_SUMMARY" | "SERVICE_ITEMS_SUMMARY" | "VEHICLE_SUMMARY";
  item: string | null;
  totalRecords: string;
  totalAmount: string;
  totalVehicles: string | null;
}

interface MaintenanceSummary {
  combined: SummaryItem;
  serviceItems: SummaryItem[];
  vehicles: SummaryItem[];
}

interface MaintenanceReportData {
  dateRange: {
    start: string;
    end: string;
    rangeType: string;
  };
  records: MaintenanceRecord[];
  summary: MaintenanceSummary;
  pagination: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
  };
}

const MaintenanceOverallReport = () => {
  const { showToast } = useToast();
  const { filterState } = useReportFilter();
  const {
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    selectedVehicleIds,
    mechanicName,
    selectedProjectIds,
  } = filterState;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Service items display state
  const [showAllServiceItems, setShowAllServiceItems] = useState(false);

  // Vehicle display state
  const [showAllVehicles, setShowAllVehicles] = useState(false);

  const {
    data: reportData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "maintenanceOverallReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
      selectedVehicleIds,
      mechanicName,
      selectedProjectIds,
      currentPage,
      pageSize,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/maintenance/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/maintenance/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      if (selectedVehicleIds.length > 0) {
        params.vehicleIds = selectedVehicleIds;
      }

      if (selectedProjectIds.length > 0) {
        params.projectIds = selectedProjectIds;
      }

      if (mechanicName) {
        params.mechanicNames = mechanicName;
      }

      // Add pagination parameters
      params.page = currentPage;
      params.limit = pageSize;

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.isSuccess) {
        return response.data.data as MaintenanceReportData;
      } else {
        showToast(response.data.message, "error");
        return null;
      }
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    timeFilter,
    customStartDate,
    customEndDate,
    selectedVehicles,
    selectedVehicleIds,
    mechanicName,
    selectedProjectIds,
  ]);

  const summary = reportData?.summary || {
    combined: {
      summaryType: "COMBINED_SUMMARY",
      item: null,
      totalRecords: "0",
      totalAmount: "0",
      totalVehicles: "0",
    },
    serviceItems: [],
    vehicles: [],
  };
  const records = reportData?.records || [];
  const pagination = reportData?.pagination;

  const { planStatus } = usePlanStatus();
  const showProjectName = [
    OperationType.CONTRACT,
    OperationType.SCHOOL,
  ].includes(planStatus?.operationType);

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = event.target.value as number;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const renderSummaryCards = () => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Expense</Typography>
          <Typography variant="h4" fontSize={15}>
            {formatCurrency(summary?.combined?.totalAmount || "0")}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Entries</Typography>
          <Typography variant="h4" fontSize={15}>
            {Number(summary?.combined?.totalRecords || 0)}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={3}>
        <Paper
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Typography fontSize={12}>Total Vehicles</Typography>
          <Typography variant="h4" fontSize={15}>
            {Number(summary?.combined?.totalVehicles || 0)}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderMaintenanceTable = (records: MaintenanceRecord[]) => (
    <Paper sx={{ p: 2 }}>
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeaderStyles}>Category</TableCell>
              {planStatus?.operationType === OperationType.LARGE_FLEET && (
                <>
                  <TableCell sx={tableHeaderStyles}>
                    {PROJECT_TERMINOLOGY.SINGULAR}
                  </TableCell>
                  <TableCell sx={tableHeaderStyles}>Division</TableCell>
                </>
              )}
              <TableCell sx={tableHeaderStyles}>Date</TableCell>
              <TableCell sx={tableHeaderStyles}>Vehicle</TableCell>
              <TableCell sx={tableHeaderStyles}>Service Type</TableCell>
              <TableCell sx={tableHeaderStyles}>Paid To</TableCell>
              <TableCell sx={tableHeaderStyles}>Location</TableCell>
              <TableCell sx={tableHeaderStyles} align="right">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow
                key={index}
                sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
              >
                <TableCell sx={tableCellStyles}>{record?.subScope || "-"}</TableCell>
                {(planStatus?.operationType === OperationType.LARGE_FLEET ||
                  planStatus?.operationType ===
                    OperationType.LARGE_FLEET_ALL) && (
                  <>
                    <TableCell sx={tableCellStyles}>
                      {record?.superProjectName}
                    </TableCell>
                    <TableCell sx={tableCellStyles}>
                      {record?.projectName || "-"}
                    </TableCell>
                  </>
                )}
                <TableCell sx={tableCellStyles}>
                  {formatDate(record?.expenseDate)}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {formatVehicleNumber(record?.vehicleNumber)}
                </TableCell>
                <TableCell sx={tableCellStyles}>
                  {record?.serviceType || "-"}
                </TableCell>
                <TableCell sx={tableCellStyles}>{record?.paidTo}</TableCell>
                <TableCell sx={tableCellStyles}>{record?.location}</TableCell>
                <TableCell sx={tableCellStyles} align="right">
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary.main"
                  >
                    {formatCurrency(record.amount)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: 80, fontSize: "0.75rem" }}
            >
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                displayEmpty
                size="small"
                sx={{ fontSize: "0.75rem" }}
              >
                <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                  20
                </MenuItem>
                <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                  50
                </MenuItem>
                <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                  100
                </MenuItem>
                <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                  200
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              {`${
                (pagination.currentPage - 1) * pagination.pageSize + 1
              }-${Math.min(
                pagination.currentPage * pagination.pageSize,
                pagination.totalCount
              )} of ${pagination.totalCount}`}
            </Typography>
          </Box>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Paper>
  );

  const renderMaintenanceChart = () => {
    if (summary?.vehicles?.length <= 1) return null;
    return (
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom fontSize={16}>
            Maintenance Expense by Vehicle
          </Typography>
          <Box
            sx={{
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#a8a8a8",
                },
              },
              "&::-webkit-scrollbar-corner": {
                backgroundColor: "#f1f1f1",
              },
              overflowX: "auto",
              overflowY: "hidden",
              width: "100%",
            }}
          >
            <Box
              sx={{
                minWidth: `${Math.max(summary.vehicles.length * 50, 600)}px`, // Dynamically calculate width
                height: "400px",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={summary.vehicles}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="item"
                    fontSize={10}
                    interval={0}
                    angle={summary.vehicles.length > 20 ? -45 : 0}
                    textAnchor={summary.vehicles.length > 20 ? "end" : "middle"}
                    height={summary.vehicles.length > 20 ? 60 : 30}
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    stroke="#8884d8"
                    fontSize={10}
                    tickFormatter={(value) => formatCurrency(value)}
                    tickCount={8}
                    domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.2)]}
                    allowDataOverflow
                  />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === "Total Amount"
                        ? formatCurrency(value)
                        : name === "Total Records"
                        ? `${value} records`
                        : value,
                      name,
                    ]}
                    labelStyle={{ fontSize: "12px" }}
                    contentStyle={{ fontSize: "11px" }}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="totalAmount"
                    name="Total Amount"
                    fill="#8884d8"
                    barSize={summary.vehicles.length < 12 ? 40 : 20}
                  >
                    <LabelList
                      dataKey="totalAmount"
                      position="top"
                      content={(props: any) => {
                        const { x, y, width, value, index } = props;
                        const data = summary.vehicles[index];
                        if (!data) return null;

                        const totalAmount = parseFloat(
                          data?.totalAmount || "0"
                        );
                        const totalRecords = parseFloat(
                          data?.totalRecords || "0"
                        );
                        const labelY = Math.max(y - 20, 30);
                        const fontSize = summary.vehicles.length > 12 ? 6 : 8;

                        return (
                          <text
                            x={x + width / 2}
                            y={labelY}
                            textAnchor="middle"
                            fontSize={fontSize}
                            fill="#333"
                          >
                            <tspan x={x + width / 2} dy="-8">
                              {formatCurrency(totalAmount)}
                            </tspan>
                            <tspan x={x + width / 2} dy="10">
                              {totalRecords} records
                            </tspan>
                          </text>
                        );
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderServiceItemsTable = () => {
    if (summary?.serviceItems?.length <= 2) return null;

    const INITIAL_DISPLAY_COUNT = 8;
    const itemsToShow = showAllServiceItems
      ? summary.serviceItems
      : summary.serviceItems.slice(0, INITIAL_DISPLAY_COUNT);

    const hasMoreItems = summary.serviceItems.length > INITIAL_DISPLAY_COUNT;

    // Calculate total and max for percentages
    const totalAmount = summary.serviceItems.reduce(
      (sum, item) => sum + parseFloat(item.totalAmount),
      0
    );
    const maxAmount = Math.max(
      ...summary.serviceItems.map((item) => parseFloat(item.totalAmount))
    );

    return (
      <Box sx={{ mb: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#323130",
              mb: 0.5,
            }}
          >
            Service Categories
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#605e5c",
              fontSize: "0.8rem",
            }}
          >
            Top maintenance expenses by category
          </Typography>
        </Box>

        {/* Power BI Style Cards Grid */}
        <Grid container spacing={1.5}>
          {itemsToShow.map((item, index) => {
            const amount = parseFloat(item.totalAmount);
            const percentage = (amount / totalAmount) * 100;
            const entries = Number(item.totalRecords);

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1,
                    bgcolor: "white",
                    border: "1px solid #e1dfdd",
                    borderRadius: 1,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      elevation: 3,
                      transform: "translateY(-1px)",
                      borderColor: "#0078d4",
                    },
                  }}
                >
                  {/* Ranking Badge */}
                  {index < 3 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor:
                          index === 0
                            ? "#0078d4"
                            : index === 1
                            ? "#00bcf2"
                            : "#40e0d0",
                        color: "white",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  {/* Content */}
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Service Name */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#323130",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        mb: 1,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.item || "MISCELLANEOUS"}
                    </Typography>

                    {/* Amount - Big Number */}
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "#323130",
                        lineHeight: 1,
                        mb: 1,
                      }}
                    >
                      {formatCurrency(amount)}
                    </Typography>

                    {/* Footer Stats */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: "#605e5c",
                          fontWeight: 500,
                        }}
                      >
                        {entries} entries
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: "#0078d4",
                          fontWeight: 600,
                        }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Show More Button */}
        {hasMoreItems && (
          <Box sx={{ mt: 1, textAlign: "center" }}>
            <Button
              variant="text"
              onClick={() => setShowAllServiceItems(!showAllServiceItems)}
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#0078d4",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "rgba(0, 120, 212, 0.04)",
                },
              }}
            >
              {showAllServiceItems
                ? `Show Less`
                : `View ${
                    summary.serviceItems.length - INITIAL_DISPLAY_COUNT
                  } More Categories`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderVehicleTable = () => {
    if (!summary?.vehicles?.length) return null;

    const INITIAL_DISPLAY_COUNT = 8;
    const vehiclesToShow = showAllVehicles
      ? summary.vehicles
      : summary.vehicles.slice(0, INITIAL_DISPLAY_COUNT);

    const hasMoreVehicles = summary.vehicles.length > INITIAL_DISPLAY_COUNT;

    // Calculate total and max for percentages
    const totalVehicleAmount = summary.vehicles.reduce(
      (sum, item) => sum + parseFloat(item.totalAmount),
      0
    );

    return (
      <Box sx={{ mb: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#323130",
              mb: 0.5,
            }}
          >
            Vehicle Performance
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#605e5c",
              fontSize: "0.8rem",
            }}
          >
            Top maintenance costs by vehicle
          </Typography>
        </Box>

        {/* Power BI Style Cards Grid */}
        <Grid container spacing={1.5}>
          {vehiclesToShow.map((item, index) => {
            const amount = parseFloat(item.totalAmount);
            const percentage = (amount / totalVehicleAmount) * 100;
            const entries = Number(item.totalRecords);

            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    bgcolor: "white",
                    border: "1px solid #e1dfdd",
                    borderRadius: 1,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      elevation: 3,
                      transform: "translateY(-1px)",
                      borderColor: "#0078d4",
                    },
                  }}
                >
                  {/* Ranking Badge */}
                  {index < 3 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        bgcolor:
                          index === 0
                            ? "#d13438"
                            : index === 1
                            ? "#ff8c00"
                            : "#ffd700",
                        color: "white",
                        borderRadius: "50%",
                        width: 18,
                        height: 18,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}

                  {/* Content */}
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Vehicle Number */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "#323130",
                        letterSpacing: 0.3,
                        mb: 0.8,
                        lineHeight: 1.2,
                        fontFamily: "monospace",
                      }}
                    >
                      {formatVehicleNumber(item.item || "N/A")}
                    </Typography>

                    {/* Amount - Big Number */}
                    <Typography
                      variant="h4"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "#323130",
                        lineHeight: 1,
                        mb: 0.8,
                      }}
                    >
                      {formatCurrency(amount)}
                    </Typography>

                    {/* Footer Stats */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#605e5c",
                          fontWeight: 500,
                        }}
                      >
                        {entries} entries
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65rem",
                          color: "#0078d4",
                          fontWeight: 600,
                        }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Show More Button */}
        {hasMoreVehicles && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Button
              variant="text"
              onClick={() => setShowAllVehicles(!showAllVehicles)}
              sx={{
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#0078d4",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "rgba(0, 120, 212, 0.04)",
                },
              }}
            >
              {showAllVehicles
                ? `Show Less`
                : `View All ${summary.vehicles.length} Vehicles`}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderNoRecords = () => {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1 }}
          align="center"
        >
          No records found
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Loading overlay for refetching */}
      {isLoading && <Loading fullScreen={false} message="Loading..." />}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing..." />
      )}
      {renderSummaryCards()}
      {(planStatus?.operationType === OperationType.LARGE_FLEET ||
        planStatus?.operationType === OperationType.LARGE_FLEET_ALL) && (
        <>
          {/* {renderMaintenanceChart()} */}
          {renderServiceItemsTable()}
        </>
      )}
      {renderVehicleTable()}
      {records?.length > 0
        ? renderMaintenanceTable(records)
        : renderNoRecords()}
    </Box>
  );
};

export default MaintenanceOverallReport;
